import { CurveEditor } from '../../ui/CurveEditor.js';
import { JsonField, NumberField, SelectField } from '../../ui/controls.js';
import { CURVE_MODES, convertCurve, curveMode, isCurveKeys, withCurveSmooth, type CurveKeys, type CurveMode } from './values.js';
import './fields.css';

/** The reusable editor (components/ui/CurveEditor.tsx), re-exported for existing imports. */
export { CurveEditor };

const SHORT_LABELS: Record<CurveMode, string> = {
  constant: 'Constant',
  random: 'Random',
  curve: 'Curve',
  randomCurves: 'Random curves',
};

/** Optional extras passed through to the curve graph. */
export interface CurveFieldExtras {
  /** Normalized time marked on the graph (e.g. a preview's playback time). */
  playhead?: number | null;
  /** What curve time means ("lifetime", "cycle"). */
  timeLabel?: string;
  /** Graph height (px). */
  height?: number;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

/**
 * Editor for a MinMaxCurve value (`5`, `[min, max]`, `{ curve, multiplier, smooth }`
 * or `{ curves: [a, b], multiplier, smooth }`) with a mode selector, or, with
 * `plain`, for bare curve keys `[[t, v], ...]`. Absent values show as an
 * empty constant.
 */
export function CurveField({
  value,
  onChange,
  step = 0.1,
  min,
  max,
  modes = CURVE_MODES,
  plain = false,
  extras = {},
}: {
  value: unknown;
  onChange: (value: unknown) => void;
  step?: number;
  min?: number;
  max?: number;
  modes?: readonly CurveMode[];
  plain?: boolean;
  extras?: CurveFieldExtras;
}) {
  const graph = {
    playhead: extras.playhead,
    timeLabel: extras.timeLabel,
    height: extras.height ?? 96,
    onGestureStart: extras.onGestureStart,
    onGestureEnd: extras.onGestureEnd,
  };
  if (plain) {
    if (value !== undefined && !isCurveKeys(value)) return <JsonField value={value} onChange={onChange} rows={2} />;
    const keys = (value as CurveKeys | undefined) ?? [];
    return (
      <div className="curvefield">
        <CurveEditor curves={[keys]} step={step} onChange={(_, k) => onChange(k)} {...graph} />
      </div>
    );
  }

  const mode = curveMode(value);
  if (mode === null) return <JsonField value={value} onChange={onChange} rows={2} />;
  const options = CURVE_MODES.filter((m) => m === mode || modes.includes(m)).map((m) => ({ value: m, label: SHORT_LABELS[m] }));
  const obj = (value ?? {}) as { curve?: CurveKeys; curves?: [CurveKeys, CurveKeys]; multiplier?: number; smooth?: boolean };
  const multiplier = obj.multiplier ?? 1;
  const setMultiplier = (m: number) => {
    const next: Record<string, unknown> = { ...obj };
    if (m === 1) delete next.multiplier;
    else next.multiplier = m;
    onChange(next);
  };

  const modeSelect = (
    <div className="curvefield-mode" title="Value mode">
      <SelectField value={mode} options={options} onChange={(m) => onChange(convertCurve(value, m as CurveMode))} />
    </div>
  );

  if (mode === 'constant') {
    return (
      <div className="curvefield">
        <div className="curvefield-row">
          <NumberField value={typeof value === 'number' ? value : undefined} step={step} min={min} max={max} onChange={onChange} />
          {modeSelect}
        </div>
      </div>
    );
  }
  if (mode === 'random') {
    const [a, b] = value as [number, number];
    return (
      <div className="curvefield">
        <div className="curvefield-row">
          <NumberField label="Min" value={a} step={step} min={min} max={max} onChange={(v) => onChange([v, b])} />
          <NumberField label="Max" value={b} step={step} min={min} max={max} onChange={(v) => onChange([a, v])} />
          {modeSelect}
        </div>
      </div>
    );
  }
  const curves = mode === 'curve' ? [obj.curve!] : obj.curves!;
  return (
    <div className="curvefield">
      <div className="curvefield-row">
        <NumberField label="×" title="Multiplier (applied to every key)" value={multiplier} step={step} onChange={setMultiplier} />
        {modeSelect}
      </div>
      <CurveEditor
        curves={curves}
        step={0.05}
        smooth={obj.smooth === true}
        onSmoothChange={(smooth) => onChange(withCurveSmooth(obj, smooth))}
        onChange={(i, keys) => {
          if (mode === 'curve') onChange({ ...obj, curve: keys });
          else {
            const next = [...obj.curves!] as [CurveKeys, CurveKeys];
            next[i] = keys;
            onChange({ ...obj, curves: next });
          }
        }}
        {...graph}
      />
    </div>
  );
}
